import styles from './AiRecommendations.module.css';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import CriteriaLegend from './CriteriaLegend';
import RecommendationRow from './RecommendationRow';
import { isBusy, mapRecommendedActor, relativeTime } from './recommendationModel';

function StateBox({ title, description, children }) {
  return (
    <div className={styles.stateBox}>
      <div className={styles.stateTitle}>{title}</div>
      {description && <div className={styles.stateDesc}>{description}</div>}
      {children}
    </div>
  );
}

/**
 * 배역 하나의 추천 결과. 상태에 따라 계산 중 / 실패 / 결과 없음 / 목록을 그린다.
 * 상단 오른쪽 actions 칸에 버튼을 더 놓으면 된다 (예: 엑셀 내보내기).
 */
export default function RoleRecommendations({ role, refreshState, timedOut, onRefresh, onReload, onOpenActor }) {
  const status = role.status ?? null;
  const busy = isBusy(status);
  const criteria = role.criteria || [];
  const actors = (role.items || [])
    .map(item => mapRecommendedActor(item))
    .filter(a => a.id != null)
    .map((a, i) => ({ ...a, rank: i + 1 }));
  const updated = relativeTime(role.updatedAt);
  const blocked = refreshState?.blockedUntil && refreshState.blockedUntil > Date.now();
  const refreshDisabled = busy || refreshState?.requesting || blocked;

  const refreshButton = (
    <button type="button" className={styles.refreshBtn} onClick={() => onRefresh(role.roleId)} disabled={refreshDisabled}>
      {refreshState?.requesting ? '요청 중…' : status == null ? '추천 받기' : '다시 추천'}
    </button>
  );

  let body;
  if (busy && actors.length === 0) {
    body = timedOut ? (
      <StateBox title="생각보다 오래 걸리고 있어요" description="계산은 서버에서 계속돼요. 잠시 뒤에 다시 불러와 주세요.">
        <button type="button" className={styles.secondaryBtn} onClick={onReload}>다시 불러오기</button>
      </StateBox>
    ) : (
      <StateBox title="AI가 배우를 고르고 있어요" description="배역 조건으로 후보를 추린 뒤 사진과 프로필을 한 명씩 평가해요. 1~2분 걸려요.">
        <div className={styles.stateSpinner}><LoadingSpinner size="sm" /></div>
      </StateBox>
    );
  } else if (status === 'FAILED') {
    body = (
      <StateBox title="추천을 계산하지 못했어요" description={role.error || '잠시 후 다시 추천을 눌러 주세요.'}>
        {refreshButton}
      </StateBox>
    );
  } else if (status == null) {
    body = (
      <StateBox title="아직 추천을 계산하지 않았어요" description="추천 받기를 누르면 이 배역에 맞는 배우를 골라 드려요.">
        {refreshButton}
      </StateBox>
    );
  } else if (actors.length === 0) {
    body = (
      <StateBox
        title="조건에 맞는 배우를 찾지 못했어요"
        description="성별·나이·키 조건을 모두 만족하는 배우가 없어요. 배역 조건을 조금 넓혀 보세요."
      />
    );
  } else {
    body = (
      <>
        {busy && (
          <div className={styles.notice}>
            {timedOut
              ? <>새 추천 계산이 오래 걸리고 있어요. <button type="button" className={styles.linkBtn} onClick={onReload}>다시 불러오기</button></>
              : '새 추천을 계산하는 중이에요. 끝나면 이 목록이 바뀌어요.'}
          </div>
        )}
        <CriteriaLegend criteria={criteria} />
        <ol className={styles.list}>
          {actors.map(actor => (
            <RecommendationRow key={actor.id} actor={actor} criteria={criteria} onOpen={a => onOpenActor(a, actors)} />
          ))}
        </ol>
      </>
    );
  }

  return (
    <section className={styles.panel} aria-label={`${role.roleName} 추천 배우`}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarMeta}>
          {busy ? '계산 중' : updated ? `${updated} 업데이트` : null}
          {!busy && actors.length > 0 && <span className={styles.toolbarCount}>· {actors.length}명</span>}
        </div>
        <div className={styles.toolbarActions}>
          {status != null && status !== 'FAILED' && refreshButton}
        </div>
      </div>
      {refreshState?.message && <div className={styles.inlineMessage} role="status">{refreshState.message}</div>}
      {body}
    </section>
  );
}
